<template>
    <AppModal ref="Modal" :title="$t('labels.editing_filter')" size="lg" @onHide="hide">
        <div class="modal-body">
            <FormGroup :label="$t('labels.filter')">
                <Input
                    :model-value="getFilterName(filter)"
                    readonly
                />
            </FormGroup>
            <div class="row mb-2">
                <div class="col-6">
                    <InputGroup>
                        <Radio
                            :model-value="filterType === 'not_variable'"
                            @update:model-value="filterType = 'not_variable'"
                        />
                        <template #append>
                            {{ $t('labels.not_variable_filter') }}
                        </template>
                    </InputGroup>
                </div>
                <div class="col-6">
                    <InputGroup>
                        <Radio
                            :model-value="filterType === 'variable'"
                            @update:model-value="filterType = 'variable'"
                        />
                        <template #append>
                            {{ $t('labels.variable_filter') }}
                        </template>
                    </InputGroup>
                </div>
            </div>
            <slot v-if="filterType === 'not_variable'">
                <FormGroup :label="$t('labels.filter_item')">
                    <CatalogAttributeSelect
                        :attribute="filter"
                        :integration="integration"
                        :category="category"
                        :model-value="filterItems"
                        @update:model-value="filterItems = $event"
                    />
                </FormGroup>
            </slot>
        </div>
        <slot v-if="filterType === 'variable'">
            <div class="table-responsive">
                <table class="table table-bordered table-fixed">
                    <thead>
                        <tr>
                            <th width="500px">{{ $t('labels.product') }}</th>
                            <th>
                                <AppNullableCell>
                                    <template #default v-if="filter">
                                        {{ filter.name }}
                                    </template>
                                </AppNullableCell>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="product in products">
                            <td>
                                <div class="d-flex product-cell">
                                    <img 
                                        class="table-image mr-2" 
                                        :src="product.picture_url ?? PLACEHOLDER_IMAGE_URL" 
                                        :alt="product.name"
                                    />
                                    <div class="text-container">
                                        <span class="d-block text-nowrap">
                                            {{ product.name }}
                                        </span>
                                        <span class="text-muted text-smaller d-block">
                                            {{ product.sku }}
                                        </span>
                                    </div>
                                </div>
                            </td>
                            <td>
                                                <CatalogAttributeSelect
                                    :attribute="filter"
                                    :integration="integration"
                                    :category="category"
                                    :model-value="product.filters[filter.uuid] || []"
                                    @update:model-value="product.filters[filter.uuid] = $event"
                                />
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </slot>
        <div class="modal-footer">
            <AppButton color="indigo" @click="saveFilter" :disabled="!canByAdded">
                {{ $t('buttons.edit') }}
            </AppButton>
        </div>
    </AppModal>
</template>
<script>
import AppModal from '../../../../components/new/modal/AppModal.vue';
import Radio from '../../../../components/new/form/radio/Radio.vue';
import FormGroup from '../../../../components/new/form/group/FormGroup.vue';
import InputGroup from '../../../../components/new/form/InputGroup.vue';
import AppButton from '../../../../components/new/button/AppButton.vue';
import AppNullableCell from '../../../components/tables/AppNullableCell.vue';
import Input from '../../../../components/new/form/Input.vue';
import CatalogAttributeSelect from '../sidebarCards/products/edit/CatalogAttributeSelect.vue';

import { httpClient } from '../../../../services/HttpClient';
import { chunk } from '../../../../utils';

export default {
    name: 'EditIntegrationGroupFilterModal',
    components: {
        AppModal, Radio, FormGroup, InputGroup, AppButton, AppNullableCell, Input,
        CatalogAttributeSelect
    },
    props: {
        category: {
            type: Object,
            required: true
        },
        integration: {
            type: Object,
            required: true
        },
        getFilterName: {
            type: Function,
            required: true
        }
    },
    emits: [
        'filterUpdated'
    ],
    data() {
        return {
            products: [],
            filter: null,
            filterItems: [],
            filterType: 'not_variable',
        }
    },
    methods: {
        async show(products, filter) {
            this.products = products;
            await this.fetchFilter(filter.filterUuid);
            
            if (this.checkFilterValuesAreSame(filter.filterUuid)) {
                this.filterType = 'not_variable'
                this.filterItems = this.products[0].filters[filter.filterUuid];
            } else {
                this.filterType = 'variable'
            }

            this.$refs.Modal.show();
        },
        hide() {
            this.$refs.Modal.hide();
            this.filter = null;
            this.filterItems = [];
            this.filterType = 'not_variable';
            this.products = [];
        },
        checkFilterValuesAreSame(filterUuid) {
            if (this.products.length === 0) return true;

            const firstValue = this.products[0].filterIdsByFilterUuids[filterUuid];

            return this.products.every(product => 
                product.filterIdsByFilterUuids[filterUuid] === firstValue
            );
        },
        async saveFilter() {
            if (this.filterType === 'variable') {
                let requestData = this.products.map(product => ({
                    method: 'PATCH',
                    path: `/integrations/products/${product.uuid}`,
                    body: {
                        filter_item_uuids: Object.values(product.filters).flat().map((filterItem) => filterItem.uuid),
                    }
                }))

                await Promise.all(chunk(requestData, 10).map(async (chunk) => {
                    await httpClient.post('/batch', {
                        data: chunk
                    });
                }));

                this.$emit('filterUpdated');
                this.hide();
                return;
            }

            if (this.filterType === 'not_variable') {
                this.products.forEach(product => {
                    product.filters[this.filter.uuid] = this.filterItems;
                });

                let requestData = this.products.map(product => ({
                    method: 'PATCH',
                    path: `/integrations/products/${product.uuid}`,
                    body: {
                        filter_item_uuids: Object.values(product.filters).flat().map((filterItem) => filterItem.uuid),
                    }
                }))

                await Promise.all(chunk(requestData, 10).map(async (chunk) => {
                    await httpClient.post('/batch', {
                        data: chunk
                    });
                }));

                this.$emit('filterUpdated');
                this.hide();
                return;
            }
        },
        async fetchFilter(filterUuid) {
            await httpClient.get(`/projects/${this.$route.params.projectUuid}/pim/channels/${this.$route.params.integrationUuid}/filters`, {
                uuids: [filterUuid]
            })
            .then(response => {
                this.filter = response.data.data[0];
            })
        }
    },
    computed: {
        canByAdded() {
            if (this.filterType === 'variable') {
                return this.products.some(product => {
                    return product.filters[this.filter.uuid] !== undefined
                })
            };

            if (this.filterType === 'not_variable') {
                return this.filterItems.length > 0;
            };

            return false
        }
    }
}
</script>
<style scoped>
.table-image {
    width: 38px;
    height: 38px;
}
.text-nowrap {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.text-container {
    min-width: 0;
    overflow: hidden;
    flex: 1;
}
.product-cell {
    max-width: 100%;
    width: 100%;
}
.text-smaller {
    font-size: 12px;
    line-height: 18px;
}
</style>